'use client';

import React from 'react';
import {
  FiInfo,
  FiAlertTriangle,
  FiXCircle,
  FiCheckCircle,
  FiBookmark,
  FiActivity
} from 'react-icons/fi'; // Switching to Feather icons for sharper lines
import { motion } from 'framer-motion';

export type CalloutType = 'info' | 'warning' | 'error' | 'success' | 'note';

const iconMap: Record<CalloutType, React.ReactNode> = {
  info: <FiInfo className="h-4 w-4" />,
  warning: <FiAlertTriangle className="h-4 w-4" />,
  error: <FiXCircle className="h-4 w-4" />,
  success: <FiCheckCircle className="h-4 w-4" />,
  note: <FiBookmark className="h-4 w-4" />,
};

const styleMap: Record<CalloutType, { border: string; bg: string; text: string; accent: string }> = {
  info: { 
    border: 'border-indigo-500/30', 
    bg: 'bg-indigo-500/5', 
    text: 'text-indigo-900 dark:text-indigo-200', 
    accent: 'text-indigo-500' 
  },
  warning: { 
    border: 'border-amber-500/30', 
    bg: 'bg-amber-500/5', 
    text: 'text-amber-900 dark:text-amber-200', 
    accent: 'text-amber-500' 
  },
  error: { 
    border: 'border-red-500/30', 
    bg: 'bg-red-500/5', 
    text: 'text-red-900 dark:text-red-200', 
    accent: 'text-red-500' 
  },
  success: { 
    border: 'border-emerald-500/30', 
    bg: 'bg-emerald-500/5', 
    text: 'text-emerald-900 dark:text-emerald-200', 
    accent: 'text-emerald-500' 
  },
  note: { 
    border: 'border-zinc-500/30', 
    bg: 'bg-zinc-500/5', 
    text: 'text-zinc-900 dark:text-zinc-300', 
    accent: 'text-zinc-500' 
  },
};

export default function Callout({
  type = 'info',
  title,
  children,
}: {
  type?: CalloutType;
  title?: string;
  children: React.ReactNode;
}) {
  const styles = styleMap[type];

  return (
    <motion.div
      initial={{ opacity: 0, x: -5 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      className={`my-10 relative overflow-hidden rounded-2xl border ${styles.border} ${styles.bg} backdrop-blur-md`}
    >
      {/* Animated Scanline Effect */}
      <motion.div 
        animate={{ y: [0, 100, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        className={`absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-${type === 'info' ? 'indigo' : type}-500/20 to-transparent opacity-30`}
      />

      <div className="flex gap-4 p-5">
        {/* Left Icon Section */}
        <div className={`flex-shrink-0 flex flex-col items-center gap-2 pt-1 ${styles.accent}`}>
          {iconMap[type]}
          <div className="w-px h-full bg-current opacity-20" />
        </div>

        {/* Content Section */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            {title ? (
              <h4 className={`text-[10px] font-black uppercase tracking-[0.3em] ${styles.accent}`}>
                {title}
              </h4>
            ) : (
              <h4 className={`text-[10px] font-black uppercase tracking-[0.3em] ${styles.accent}`}>
                SYSTEM_{type.toUpperCase()}
              </h4>
            )}
            
            {/* Tactical Status Tag */}
            <div className="flex items-center gap-2 opacity-40">
              <FiActivity size={10} className="animate-pulse" />
              <span className="text-[9px] font-mono font-bold tracking-widest">SIGNAL_STABLE</span>
            </div>
          </div>

          <div className={`text-sm leading-relaxed font-medium ${styles.text}`}>
            {children}
          </div>
        </div>
      </div>

      {/* Subtle Corner Accents */}
      <div className={`absolute top-0 right-0 w-8 h-8 opacity-20 border-t-2 border-r-2 rounded-tr-2xl ${styles.border}`} />
    </motion.div>
  );
}